// reportModel — pure computation behind the CRM Reports tab.
//
// No React, no DOM: `buildReportModel()` takes deals/contracts/stages plus
// the report settings and returns every number the paper renders. Kept
// separate from CRMReports.jsx so it can be unit-tested from Node and so
// the component file only exports components (react-refresh rule).

export const MONTHS      = ['January','February','March','April','May','June','July','August','September','October','November','December'];
export const MONTH_SHORT = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];

export const money   = (v) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(Number(v) || 0);
export const pct     = (v) => Number.isFinite(v) ? `${Math.round(v)}%` : '—';
export const fmtDate = (d) => d ? d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' }) : '—';
export const todayIso = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`; };

// Parse 'YYYY-MM-DD' as a LOCAL date (new Date('2026-01-01') is UTC
// midnight and shifts a day west of Greenwich). Falls back to Date().
export function parseDate(s) {
  if (!s) return null;
  if (s instanceof Date) return isNaN(s) ? null : s;
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(s));
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]);
  const d = new Date(s);
  return isNaN(d) ? null : d;
}
const startOfMonth = (d) => new Date(d.getFullYear(), d.getMonth(), 1);
const endOfMonth   = (d) => new Date(d.getFullYear(), d.getMonth() + 1, 0);
const monthKey     = (d) => `${d.getFullYear()}-${d.getMonth()}`;

function monthsBetween(start, end) {
  const out = [];
  let cur = startOfMonth(start);
  while (cur <= end) {
    out.push({ key: monthKey(cur), y: cur.getFullYear(), m: cur.getMonth(), label: `${MONTH_SHORT[cur.getMonth()]} ${String(cur.getFullYear()).slice(2)}` });
    cur = new Date(cur.getFullYear(), cur.getMonth() + 1, 1);
  }
  return out;
}

export function fiscalWindow(asOf, startMonth) {
  const y = asOf.getMonth() >= startMonth ? asOf.getFullYear() : asOf.getFullYear() - 1;
  const fyStart = new Date(y, startMonth, 1);
  const fyEnd   = new Date(y + 1, startMonth, 0);
  const label   = startMonth === 0 ? `FY${y}` : `FY${y}–${String(y + 1).slice(2)}`;
  return { fyStart, fyEnd, label };
}

/**
 * Annual recurring revenue currently in force.
 *
 * Rule: a won deal's ARR is counted ONCE. If the deal has a contract, the
 * contract decides (counted while active/renewed, dropped when expired or
 * cancelled). If the deal has no contract yet — bulk-entered wins, imports —
 * the deal's own ARR counts so a 200k/yr win shows up the moment it is
 * entered. Contracts with no deal behind them count on their own status.
 * Returns { total, newInPeriod(start,end) } so callers share one rule.
 */
export function computeArr(deals = [], contracts = []) {
  const inForce = (c) => c.status === 'active' || c.status === 'renewed';
  const byDeal = new Map();
  for (const c of contracts) if (c.dealId) byDeal.set(c.dealId, c);
  const entries = []; // { arr, start }
  for (const d of deals) {
    if (d.stage !== 'closed_won') continue;
    const c = byDeal.get(d.id);
    if (c) { if (inForce(c)) entries.push({ arr: Number(c.arr) || 0, start: c.startDate || d.wonDate || d.closeDate || null }); }
    else entries.push({ arr: Number(d.arr) || 0, start: d.wonDate || d.closeDate || null });
  }
  const dealIds = new Set(deals.map(d => d.id));
  for (const c of contracts) {
    if (c.dealId && dealIds.has(c.dealId)) continue;   // already handled via its deal
    if (inForce(c)) entries.push({ arr: Number(c.arr) || 0, start: c.startDate || null });
  }
  const total = entries.reduce((s, e) => s + e.arr, 0);
  const newInPeriod = (start, end) => entries
    .filter(e => { const d = parseDate(e.start); return d && d >= start && d <= end; })
    .reduce((s, e) => s + e.arr, 0);
  return { total, newInPeriod };
}

export const DEFAULT_REPORT_SETTINGS = {
  title:                'Sales Performance Report',
  fiscalYearStartMonth: 0,
  asOf:                 todayIso(),
  horizon:              'fy',        // 'fy' | '3' | '6' | '12'
  commitThreshold:      75,
  upsideThreshold:      50,
  ownerUserId:          '',
  preparedBy:           '',
  commentary:           '',
  sections:             { ytd: true, forecast: true, funnel: true, deals: true },
};

export function buildReportModel({ deals, contracts, stages, settings, users }) {
  const asOf = parseDate(settings.asOf) || new Date();
  const { fyStart, fyEnd, label: fyLabel } = fiscalWindow(asOf, Number(settings.fiscalYearStartMonth) || 0);
  const inYtd = (d) => d && d >= fyStart && d <= asOf;
  const userName = (id) => (users || []).find(u => u.id === id)?.name || null;

  const owner = settings.ownerUserId || '';
  const scoped = owner ? deals.filter(d => d.ownerUserId === owner) : deals;

  const stageIdx = Object.fromEntries(stages.map((s, i) => [s.id, i]));
  const labelIdx = Object.fromEntries(stages.map((s, i) => [s.label.toLowerCase(), i]));
  const pipelineStages = stages.filter(s => s.id !== 'closed_lost');

  // Actual close: won deals carry an explicit won_date (backfilled from
  // close_date for older rows); everything else falls back to the
  // expected close, then the last update.
  const closeOf = (d) => parseDate(d.wonDate) || parseDate(d.closeDate) || parseDate(d.updatedAt) || parseDate(d.createdAt);
  const isWon  = (d) => d.stage === 'closed_won';
  const isLost = (d) => d.stage === 'closed_lost';
  const isOpen = (d) => !isWon(d) && !isLost(d);
  const val    = (d) => Number(d.value) || 0;
  const prob   = (d) => Math.min(100, Math.max(0, Number(d.probability) || 0));
  const weighted = (d) => val(d) * prob(d) / 100;

  // Furthest stage a deal reached — for lost deals we read the activity
  // log ("Advanced to Proposal Out") since `stage` only says closed_lost.
  const furthest = (d) => {
    if (!isLost(d)) return stageIdx[d.stage] ?? 0;
    let best = 0;
    for (const a of d.activities || []) {
      const m = /advanced to (.+)$/i.exec(a?.note || '');
      if (m) best = Math.max(best, labelIdx[m[1].trim().toLowerCase()] ?? 0);
    }
    return best;
  };

  // ── YTD ──────────────────────────────────────────────────────
  const won     = scoped.filter(isWon);
  const lost    = scoped.filter(isLost);
  const open    = scoped.filter(isOpen);
  const wonYtd  = won.filter(d => inYtd(closeOf(d)));
  const lostYtd = lost.filter(d => inYtd(closeOf(d)));
  const newYtd  = scoped.filter(d => inYtd(parseDate(d.createdAt)));
  const wonYtdValue = wonYtd.reduce((s, d) => s + val(d), 0);
  const winRateYtd  = (wonYtd.length + lostYtd.length) ? wonYtd.length / (wonYtd.length + lostYtd.length) * 100 : NaN;
  const winRateAll  = (won.length + lost.length) ? won.length / (won.length + lost.length) * 100 : NaN;
  const avgDealYtd  = wonYtd.length ? wonYtdValue / wonYtd.length : 0;
  const cycleDays   = wonYtd.map(d => { const a = parseDate(d.createdAt), b = closeOf(d); return a && b ? (b - a) / 86400000 : null; }).filter(x => x !== null && x >= 0);
  const avgCycle    = cycleDays.length ? cycleDays.reduce((s, x) => s + x, 0) / cycleDays.length : NaN;

  // ARR follows the shared rule in computeArr (contract if present, else the
  // won deal's own ARR; nothing expired or cancelled). New ARR = the same
  // entries whose term started inside the YTD window.
  const arr = computeArr(scoped, contracts || []);
  const totalArr  = arr.total;
  const newArrYtd = arr.newInPeriod(fyStart, asOf);

  const ytdMonths = monthsBetween(fyStart, asOf).map(m => {
    const w = wonYtd.filter(d => { const c = closeOf(d); return c && monthKey(c) === m.key; });
    const n = newYtd.filter(d => { const c = parseDate(d.createdAt); return c && monthKey(c) === m.key; });
    const l = lostYtd.filter(d => { const c = closeOf(d); return c && monthKey(c) === m.key; });
    return { ...m, wonValue: w.reduce((s, d) => s + val(d), 0), wonCount: w.length, newCount: n.length, lostCount: l.length };
  });
  let running = 0;
  for (const m of ytdMonths) { running += m.wonValue; m.cumulative = running; }

  // ── Forecast ─────────────────────────────────────────────────
  const commitT = Number(settings.commitThreshold) || 75;
  const upsideT = Number(settings.upsideThreshold) || 50;
  const horizonEnd = settings.horizon === 'fy'
    ? fyEnd
    : endOfMonth(new Date(asOf.getFullYear(), asOf.getMonth() + (Number(settings.horizon) || 3), 1));
  const horizonLabel = settings.horizon === 'fy' ? `rest of ${fyLabel}` : `next ${settings.horizon} months`;
  const category = (d) => prob(d) >= commitT ? 'commit' : prob(d) >= upsideT ? 'upside' : 'pipeline';

  const fcMonths = monthsBetween(startOfMonth(asOf), horizonEnd).map(m => ({ ...m, commit: 0, upside: 0, pipeline: 0, weighted: 0, count: 0, deals: [] }));
  const byKey = Object.fromEntries(fcMonths.map(m => [m.key, m]));
  const overdue = [], unscheduled = [], beyond = [];
  for (const d of open) {
    const cd = parseDate(d.closeDate);
    if (!cd) { unscheduled.push(d); continue; }
    if (cd < startOfMonth(asOf)) { overdue.push(d); continue; }
    const b = byKey[monthKey(cd)];
    if (!b) { beyond.push(d); continue; }
    b[category(d)] += val(d);
    b.weighted += weighted(d);
    b.count += 1;
    b.deals.push(d);
  }
  const sum = (arr, f) => arr.reduce((s, x) => s + f(x), 0);
  const fcTotals = {
    commit:   sum(fcMonths, m => m.commit),
    upside:   sum(fcMonths, m => m.upside),
    pipeline: sum(fcMonths, m => m.pipeline),
    weighted: sum(fcMonths, m => m.weighted),
    count:    sum(fcMonths, m => m.count),
  };
  const inFy = (d) => { const cd = parseDate(d.closeDate); return cd && cd >= startOfMonth(asOf) && cd <= fyEnd; };
  const fyWeightedRemaining = sum(open.filter(inFy), weighted);
  const fyCommitRemaining   = sum(open.filter(d => inFy(d) && category(d) !== 'pipeline'), val);
  const projection = {
    ytd:          wonYtdValue,
    weighted:     wonYtdValue + fyWeightedRemaining,
    bestCase:     wonYtdValue + fyCommitRemaining,
    openTotal:    sum(open, val),
    openWeighted: sum(open, weighted),
  };

  // ── Funnel ───────────────────────────────────────────────────
  const funnel = pipelineStages.map((s, i) => {
    const at = scoped.filter(d => d.stage === s.id);
    const reached = scoped.filter(d => furthest(d) >= i || isWon(d)).length;
    const lostHere = lost.filter(d => furthest(d) === i).length;
    return { ...s, idx: i, count: at.length, value: sum(at, val), weighted: sum(at, weighted), reached, lostHere };
  });
  for (let i = 0; i < funnel.length; i++) {
    const next = funnel[i + 1];
    funnel[i].conversion = next && funnel[i].reached ? next.reached / funnel[i].reached * 100 : NaN;
  }
  const maxReached = Math.max(1, ...funnel.map(f => f.reached));
  const maxStageValue = Math.max(1, ...funnel.map(f => f.value));

  // ── Appendix ─────────────────────────────────────────────────
  const stageLabel = (id) => stages.find(s => s.id === id)?.label || id;
  const openDeals = [...open].sort((a, b) => weighted(b) - weighted(a)).map(d => ({
    id: d.id, title: d.title, company: d.company || '—', stage: stageLabel(d.stage), owner: userName(d.ownerUserId) || '—',
    value: val(d), probability: prob(d), weighted: weighted(d), closeDate: parseDate(d.closeDate), category: category(d),
  }));
  const wonDealsYtd = [...wonYtd].sort((a, b) => (closeOf(b) || 0) - (closeOf(a) || 0)).map(d => ({
    id: d.id, title: d.title, company: d.company || '—', owner: userName(d.ownerUserId) || '—', value: val(d), closeDate: closeOf(d),
  }));

  return {
    asOf, fyStart, fyEnd, fyLabel, ownerName: owner ? (userName(owner) || 'Selected owner') : null,
    ytd: { wonValue: wonYtdValue, wonCount: wonYtd.length, lostCount: lostYtd.length, newCount: newYtd.length, winRate: winRateYtd, winRateAll, avgDeal: avgDealYtd, avgCycle, totalArr, newArr: newArrYtd, months: ytdMonths, maxMonth: Math.max(1, ...ytdMonths.map(m => m.wonValue)) },
    forecast: { months: fcMonths, totals: fcTotals, overdue, unscheduled, beyond, horizonLabel, horizonEnd, commitT, upsideT, projection, maxMonth: Math.max(1, ...fcMonths.map(m => m.commit + m.upside + m.pipeline)) },
    funnel: { rows: funnel, maxReached, maxStageValue, openCount: open.length, wonCount: won.length, lostCount: lost.length },
    openDeals, wonDealsYtd,
  };
}
